import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { auth, db } from '../lib/firebase';
import { Calendar, Clock, Plus, Check, RefreshCw, ChevronRight, PlayCircle, Zap, Trash2, Users as UsersIcon, Info, CalendarClock, StopCircle, X } from 'lucide-react';
import { createGame, subscribeGames, startGame, getCompletedGames, deleteGame, deleteMultipleSessions, duplicateMultipleSessions } from '../lib/gameService';
import { TopNav } from '../components/TopNav';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc, writeBatch, query, where, orderBy, limit } from 'firebase/firestore';
import { PrecisionTimePicker } from '../components/PrecisionTimePicker';
import { ConfirmModal } from '../components/ConfirmModal';

// ─── Timer Hooks ──────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');

function useCountdown(isoTarget) {
    const calc = useCallback(() => {
        const diff = Math.max(0, new Date(isoTarget) - Date.now());
        const s = Math.floor(diff / 1000);
        return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
    }, [isoTarget]);

    const [parts, setParts] = useState(calc);
    useEffect(() => {
        setParts(calc());
        const id = setInterval(() => setParts(calc()), 1000);
        return () => clearInterval(id);
    }, [calc]);
    return parts;
}

function useStopwatch(isoStart) {
    const calc = useCallback(() => {
        if (!isoStart) return { h: 0, m: 0, s: 0 };
        const elapsed = Math.max(0, Date.now() - new Date(isoStart));
        const totalSec = Math.floor(elapsed / 1000);
        return { h: Math.floor(totalSec / 3600), m: Math.floor((totalSec % 3600) / 60), s: totalSec % 60 };
    }, [isoStart]);

    const [parts, setParts] = useState(calc);
    useEffect(() => {
        if (!isoStart) return;
        setParts(calc());
        const id = setInterval(() => setParts(calc()), 1000); // 1s tick for seconds precision
        return () => clearInterval(id);
    }, [calc, isoStart]);
    return parts;
}

// ─── New Session Modal ────────────────────────────────────────────
function NewSessionModal({ onClose, onCreated }) {
    const { t } = useLanguage();
    const hostId = auth.currentUser?.uid;
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    
    // Form State
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [time, setTime] = useState('12:00'); // Default for circular picker
    const [capacity, setCapacity] = useState('1 Table');
    const [recurrence, setRecurrence] = useState('One-time');
    
    // Config states
    const [table1Config, setTable1Config] = useState({ variant: 'No Limit Holdem', stakes: '1/2', customStakes: '', minBuyIn: '200' });
    const [table2Config, setTable2Config] = useState({ variant: 'No Limit Holdem', stakes: '1/2', customStakes: '', minBuyIn: '200' });
    
    // Presets from history
    const [lastSessionTime, setLastSessionTime] = useState(null);
    const [mostChosenTime, setMostChosenTime] = useState(null);
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRestaurants, setSelectedRestaurants] = useState([]);
    const [confirmationType, setConfirmationType] = useState('simple');
    const [autoWaitlist, setAutoWaitlist] = useState(true);
    const [autoSettle, setAutoSettle] = useState(false);


    useEffect(() => {
        if (!hostId) return;
        
        // Fetch last 50 games for stats
        const q = query(
            collection(db, `hosts/${hostId}/games`),
            orderBy('createdAt', 'desc'),
            limit(50)
        );
        getDocs(q).then(snap => {
            const games = snap.docs.map(d => d.data());
            if (games.length > 0) {
                // Last session time
                const last = games[0].startTime;
                if (last) {
                    setLastSessionTime(dayjs(last).format('HH:mm'));
                }

                // Most chosen time
                const times = games
                    .map(g => dayjs(g.startTime).format('HH:mm'))
                    .filter(t => t !== 'Invalid Date');
                
                if (times.length > 0) {
                    const counts = {};
                    let maxCount = 0;
                    let mostChosen = null;
                    times.forEach(t => {
                        counts[t] = (counts[t] || 0) + 1;
                        if (counts[t] > maxCount) {
                            maxCount = counts[t];
                            mostChosen = t;
                        }
                    });
                    setMostChosenTime(mostChosen);
                }
            }
        });

        getDocs(collection(db, `hosts/${hostId}/restaurants`)).then(snap => {
            setRestaurants(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
    }, [hostId]);

    const handleCreate = async () => {
        if (!hostId || !date || !time) return;
        setLoading(true);
        try {
            const startTime = new Date(`${date}T${time}`).toISOString();
            
            // Normalize configs
            const t1Final = { ...table1Config, stakes: table1Config.stakes === 'Other' ? table1Config.customStakes : table1Config.stakes };
            const t2Final = { ...table2Config, stakes: table2Config.stakes === 'Other' ? table2Config.customStakes : table2Config.stakes };

            await createGame(hostId, {
                startTime,
                isRecurring: recurrence,
                capacity,
                attachedRestaurants: selectedRestaurants,
                confirmationType,
                table1Config: t1Final,
                table2Config: capacity === '2 Tables' ? t2Final : null,
                autoWaitlist,
                autoSettle
            });
            onCreated?.();
            onClose();
        } catch (err) {
            console.error('Error creating session:', err);
        } finally {
            setLoading(false);
        }
    };

    // Calculate max steps dynamically based on capacity
    const maxSteps = capacity === '2 Tables' ? 6 : 5;

    const next = () => setStep(s => Math.min(s + 1, maxSteps));
    const back = () => setStep(s => Math.max(s - 1, 1));

    const renderTableConfig = (config, setConfig, titleLabel) => (
        <div className="space-y-4 animate-in slide-in-from-left-4 duration-300">
             <div className="flex items-center gap-2 mb-2">
               <Zap className="w-4 h-4 text-purple-500" />
               <h3 className="text-sm font-bold text-white">{titleLabel} {t('configuration')}</h3>
            </div>
            
            <div className="space-y-1.5">
                <label className="text-[10px] text-slate-500 uppercase font-black">{t('variant')}</label>
                <div className="grid grid-cols-2 gap-2">
                    {['No Limit Holdem', 'Omaha PLO'].map(v => (
                        <button key={v} onClick={() => setConfig({...config, variant: v})} className={clsx("py-2.5 rounded-lg border text-xs font-bold transition-all", config.variant === v ? "bg-[#7C3AED] border-[#7C3AED] text-white" : "bg-slate-800 border-slate-700 text-slate-400")}>
                            {v.includes('Holdem') ? t('holdem') : t('omaha')}
                        </button>
                    ))}
                </div>
            </div>

            <div className="space-y-1.5">
                <label className="text-[10px] text-slate-500 uppercase font-black">{t('stakes')}</label>
                <div className="grid grid-cols-3 gap-2">
                    {['1/2', '2/5', '5/5', '5/10', '10/25', 'Other'].map(s => (
                        <button key={s} onClick={() => setConfig({...config, stakes: s})} className={clsx("py-2.5 rounded-lg border text-xs font-bold transition-all", config.stakes === s ? "bg-[#7C3AED] border-[#7C3AED] text-white" : "bg-slate-800 border-slate-700 text-slate-400")}>
                            {s}
                        </button>
                    ))}
                </div>
                {config.stakes === 'Other' && (
                    <input type="text" placeholder={t('customStakesPlaceholder')} value={config.customStakes} onChange={e => setConfig({...config, customStakes: e.target.value})} className="w-full mt-2 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[#7C3AED]" />
                )}
            </div>

            <div className="space-y-1.5">
                 <label className="text-[10px] text-slate-500 uppercase font-black">{t('minBuyinAmount')}</label>
                 <input type="number" value={config.minBuyIn} onChange={e => setConfig({...config, minBuyIn: e.target.value})} className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[#7C3AED] transition-all font-mono" />
            </div>
        </div>
    );

    const renderStep = () => {
        if (step === 1) {
            return (
                <div className="space-y-4 animate-in slide-in-from-left-4 duration-300">
                    <div className="flex items-center gap-2 mb-2">
                       <Calendar className="w-4 h-4 text-purple-500" />
                       <h3 className="text-sm font-bold text-white">{t('stepDateTime')}</h3>
                    </div>
                    <div className="flex flex-col gap-4">
                        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-purple-500 transition-all font-heebo [color-scheme:dark]" />
                        <div className="flex justify-center mt-2 w-full">
                            <PrecisionTimePicker 
                                value={time} 
                                onChange={setTime} 
                                lastSessionTime={lastSessionTime}
                                mostChosenTime={mostChosenTime}
                                activeColor="#7C3AED"
                            />
                        </div>
                    </div>
                </div>
            );
        }
        
        if (step === 2) {
            return (
                <div className="space-y-4 animate-in slide-in-from-left-4 duration-300">
                     <div className="flex items-center gap-2 mb-2">
                       <UsersIcon className="w-4 h-4 text-purple-500" />
                       <h3 className="text-sm font-bold text-white">{t('stepCapacityTitle')}</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            { id: '1 Table', label: t('oneTable'), sub: t('upTo11') },
                            { id: '2 Tables', label: t('twoTables'), sub: t('upTo22') }
                        ].map(opt => (
                            <button key={opt.id} onClick={() => setCapacity(opt.id)} className={clsx("py-6 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 text-center", capacity === opt.id ? "bg-purple-500/10 border-purple-500 text-purple-500" : "bg-slate-800/50 border-white/5 text-slate-500 hover:border-white/10")}>
                                <span className="text-lg font-black">{opt.label}</span>
                                <span className="text-[10px] uppercase font-bold opacity-60 px-2">{opt.sub}</span>
                            </button>
                        ))}
                    </div>
                </div>
            );
        }
        
        if (step === 3) {
            return renderTableConfig(table1Config, setTable1Config, t('table1'));
        }

        if (capacity === '2 Tables' && step === 4) {
            return renderTableConfig(table2Config, setTable2Config, t('table2'));
        }

        const recurrenceStepNum = capacity === '2 Tables' ? 5 : 4;
        const reviewStepNum = capacity === '2 Tables' ? 6 : 5;

        if (step === recurrenceStepNum) {
            return (
                <div className="space-y-4 animate-in slide-in-from-left-4 duration-300">
                     <div className="flex items-center gap-2 mb-2">
                       <RefreshCw className="w-4 h-4 text-purple-500" />
                       <h3 className="text-sm font-bold text-white">{t('stepRecurrenceTitle').replace('{{num}}', recurrenceStepNum)}</h3>
                    </div>
                    <div className="space-y-2">
                        {[
                            { id: 'One-time', label: t('oneTimeLabel') },
                            { id: 'Every Week', label: t('everyWeekLabel') },
                            { id: 'Every Week (Specific Days)', label: t('everyWeekSpecificDays') },
                            { id: 'Custom Schedule', label: t('customSchedule') }
                        ].map(r => (
                            <button key={r.id} onClick={() => setRecurrence(r.id)} className={clsx("w-full py-4 rounded-xl border transition-all px-5 flex items-center justify-between", recurrence === r.id ? "bg-purple-500/10 border-purple-500 text-purple-500" : "bg-slate-800/50 border-white/5 text-slate-500")}>
                                <span className="font-bold">{r.label}</span>
                                {recurrence === r.id && <div className="w-2 h-2 rounded-full bg-purple-500 shadow-[0_0_8px_rgba(124,58,237,0.5)]" />}
                            </button>
                        ))}
                    </div>
                </div>
            );
        }

        if (step === reviewStepNum) {
            return (
                <div className="space-y-6 animate-in slide-in-from-left-4 duration-300">
                     <div className="flex items-center gap-2 mb-2">
                       <span className="text-lg">🍽️</span>
                       <h3 className="text-sm font-bold text-white">{t('stepHospitalityTitle').replace('{{num}}', reviewStepNum)}</h3>
                    </div>
                    
                     <div className="bg-slate-800/50 rounded-2xl p-4 border border-white/5">
                          <label className="block text-[11px] text-slate-400 mb-3 font-heebo uppercase font-black">{t('conciergeAutomation')}</label>
                          <div className="grid grid-cols-2 gap-3 mb-4 border-b border-white/5 pb-4">
                             <button onClick={() => setConfirmationType('simple')} className={clsx("py-2.5 rounded-xl text-[10px] font-black border transition-all", confirmationType === 'simple' ? "bg-[#7C3AED] border-[#7C3AED] text-white shadow-lg" : "bg-slate-800 border-slate-700 text-slate-500")}>
                                {t('arrivalConfirmation')}
                             </button>
                             <button onClick={() => setConfirmationType('meal')} className={clsx("py-2.5 rounded-xl text-[10px] font-black border transition-all", confirmationType === 'meal' ? "bg-[#7C3AED] border-[#7C3AED] text-white shadow-lg" : "bg-slate-800 border-slate-700 text-slate-500")}>
                                {t('mealSelection')}
                             </button>
                          </div>

                          <div className="space-y-4">
                              <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-2xl border border-white/5">
                                  <div className="flex items-center gap-2">
                                      <div className="flex flex-col">
                                          <span className="text-xs font-bold text-white uppercase tracking-tight">{t('waitlistAutomation')}</span>
                                          <span className="text-[9px] text-slate-500">{t('waitlistAutomationDesc')}</span>
                                      </div>
                                      <div className="group relative">
                                          <Info className="w-3.5 h-3.5 text-slate-600 hover:text-purple-500 transition-colors cursor-help" />
                                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 p-2 bg-slate-800 border border-white/10 rounded-xl text-[10px] text-slate-300 shadow-2xl z-50 pointer-events-none font-heebo">
                                              {t('conciergeDesc')}
                                          </div>
                                      </div>
                                  </div>
                                  <button onClick={() => setAutoWaitlist(!autoWaitlist)} className={clsx("w-10 h-5 rounded-full transition-all relative", autoWaitlist ? "bg-emerald-500" : "bg-slate-700")}>
                                      <div className={clsx("absolute top-1 w-3 h-3 rounded-full bg-white transition-all", autoWaitlist ? "left-6" : "left-1")} />
                                  </button>
                              </div>
                              <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-2xl border border-white/5">
                                  <div className="flex items-center gap-2">
                                      <div className="flex flex-col">
                                          <span className="text-xs font-bold text-white uppercase tracking-tight">{t('seatSettlement')}</span>
                                          <span className="text-[9px] text-slate-500">{t('seatSettlementDesc')}</span>
                                      </div>
                                      <div className="group relative">
                                          <Info className="w-3.5 h-3.5 text-slate-600 hover:text-purple-500 transition-colors cursor-help" />
                                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 p-2 bg-slate-800 border border-white/10 rounded-xl text-[10px] text-slate-300 shadow-2xl z-50 pointer-events-none font-heebo">
                                              {t('seatSettlementTooltip')}
                                          </div>
                                      </div>
                                  </div>
                                  <button onClick={() => setAutoSettle(!autoSettle)} className={clsx("w-10 h-5 rounded-full transition-all relative", autoSettle ? "bg-emerald-500" : "bg-slate-700")}>
                                      <div className={clsx("absolute top-1 w-3 h-3 rounded-full bg-white transition-all", autoSettle ? "left-6" : "left-1")} />
                                  </button>
                              </div>
                          </div>
                     </div>

                    {restaurants.length > 0 && confirmationType === 'meal' && (
                        <div className="bg-slate-800/50 rounded-2xl p-4 border border-white/5">
                            <label className="block text-[11px] text-slate-400 mb-2 font-heebo uppercase font-black">{t('selectRestaurants')}</label>
                            <div className="flex flex-wrap gap-2">
                                {restaurants.map(res => (
                                    <button key={res.id} onClick={() => {
                                        if (selectedRestaurants.includes(res.id)) {
                                            setSelectedRestaurants(prev => prev.filter(id => id !== res.id));
                                        } else if (selectedRestaurants.length < 5) {
                                            setSelectedRestaurants(prev => [...prev, res.id]);
                                        }
                                    }} className={clsx("px-3 py-1.5 rounded-full text-[10px] font-bold border transition-all", selectedRestaurants.includes(res.id) ? "bg-[#7C3AED] text-white border-[#7C3AED]" : "bg-slate-800 border-slate-700 text-slate-500")}>
                                        {res.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    <div className="bg-purple-500/5 p-5 rounded-3xl border border-purple-500/20">
                        <h4 className="text-[11px] text-purple-500 font-black uppercase mb-3 tracking-widest">{t('reviewSummary')}</h4>
                        <div className="text-sm text-slate-300 space-y-2">
                            <p className="flex justify-between"><span className="text-slate-500">{t('timingLabel')}</span> <span className="font-bold text-white tabular-nums">{date} @ {time}</span></p>
                            <p className="flex justify-between"><span className="text-slate-500">{t('capacityLabel')}</span> <span className="font-bold text-white tracking-tight">{capacity === '2 Tables' ? t('twoTables') : t('oneTable')}</span></p>
                            <p className="flex justify-between"><span className="text-slate-500">{t('automationLabel')}</span> <span className="font-bold text-purple-400">{confirmationType === 'simple' ? t('arrivalConfirmation') : t('mealSelection')}</span></p>
                            <p className="flex justify-between border-t border-white/5 pt-2 mt-2"><span className="text-slate-500">T1:</span> <span className="text-white">{table1Config.variant} ({table1Config.stakes === 'Other' ? table1Config.customStakes : table1Config.stakes})</span></p>
                            {capacity === '2 Tables' && (
                                <p className="flex justify-between"><span className="text-slate-500">T2:</span> <span className="text-white">{table2Config.variant} ({table2Config.stakes === 'Other' ? table2Config.customStakes : table2Config.stakes})</span></p>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/90 backdrop-blur-md p-0 sm:p-4">
            <div className="w-full sm:max-w-md bg-slate-900 border border-white/10 rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
                    <div className="flex items-center gap-3">
                        <div className="flex gap-1">
                            {Array.from({ length: maxSteps }).map((_, i) => {
                                const s = i + 1;
                                return (
                                    <div key={s} className={clsx("w-2 h-1 rounded-full transition-all", s === step ? "bg-[#7C3AED] w-4" : s < step ? "bg-[#7C3AED]/40" : "bg-slate-700")} />
                                );
                            })}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-white transition-colors rounded-xl hover:bg-white/5">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                
                <div className="p-6 flex-1 overflow-y-auto no-scrollbar min-h-[350px]">
                    {renderStep()}
                </div>

                <div className="p-6 border-t border-white/5 bg-slate-950/50 flex gap-3">
                    {step > 1 && (
                        <button onClick={back} className="flex-1 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-2xl transition-all">
                            {t('back')}
                        </button>
                    )}
                    {step < maxSteps ? (
                        <button onClick={next} className="flex-[2] py-4 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-black rounded-2xl transition-all shadow-lg shadow-purple-500/20">
                            {t('continue')}
                        </button>
                    ) : (
                        <button onClick={handleCreate} disabled={loading} className="flex-[2] py-4 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black rounded-2xl transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50">
                            {loading ? t('creating') : t('createSession')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
// ─── Card Sub-components ──────────────────────────────────────────

function ActiveCard({ session, onNavigate, onDelete, isSelected, onSelect, hasSelection }) {
    const { t, language, isRtl } = useLanguage();
    const { h, m } = useStopwatch(session.startedAt || session.startTime);
    const dateStr = new Date(session.startTime).toLocaleDateString(
        language === 'he' ? 'he-IL' : 'en-US',
        { weekday: 'short', day: 'numeric', month: 'short' }
    );
    const timeStr = new Date(session.startTime).toLocaleTimeString(
        language === 'he' ? 'he-IL' : 'en-US',
        { hour: '2-digit', minute: '2-digit' }
    );
    const isMultiTable = session.capacity === '2 Tables';
    const variantStr = isMultiTable
        ? `${session.table1Config?.variant?.includes('PLO') ? 'PLO' : 'NLH'} / ${session.table2Config?.variant?.includes('PLO') ? 'PLO' : 'NLH'}`
        : session.variant?.includes('PLO') ? 'PLO' : 'NLH';
    const stakesStr = isMultiTable
        ? `${session.table1Config?.stakes || '?'} & ${session.table2Config?.stakes || '?'}`
        : session.stakes;

    return (
        <div className={clsx(
            'w-full relative flex items-stretch rounded-3xl overflow-hidden transition-all cursor-pointer bg-slate-900 border active:scale-[0.99] group shadow-2xl hover:shadow-[0_0_30px_rgba(52,211,153,0.15)]',
            isSelected ? 'border-emerald-500 ring-2 ring-emerald-500/20' : 'border-emerald-500/20'
        )} onClick={() => { if (hasSelection) onSelect(session.id); else onNavigate(session.id); }}>

            {/* Selection checkbox */}
            <div className={clsx("absolute top-4 z-20", isRtl ? "right-4" : "left-4")}>
                <button onClick={e => { e.stopPropagation(); onSelect(session.id); }}
                    className={clsx('w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all',
                        (hasSelection || isSelected) ? 'opacity-100 scale-110' : 'opacity-20 hover:opacity-100',
                        isSelected ? 'bg-emerald-500 border-emerald-500' : 'bg-slate-800/80 border-slate-700'
                    )}>
                    {isSelected && <Check className="w-4 h-4 text-slate-950 font-black" />}
                </button>
            </div>

            {/* ── Status Strip (Right in RTL, Left in LTR) ── */}
            {!isRtl && (
                <div className="w-[4.5rem] flex-shrink-0 bg-emerald-500/10 border-r-[3px] border-r-emerald-500 flex flex-col items-center justify-center py-4 px-1 gap-1 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 via-transparent to-transparent" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_12px_rgba(52,211,153,0.8)]" />
                    <span className="text-[12px] tracking-tight font-black text-emerald-400 tabular-nums leading-none mt-1">{pad(h)}:{pad(m)}</span>
                    <span className="text-[8px] font-black text-emerald-300/80 tracking-[0.2em] uppercase mt-1">{t('live')}</span>
                </div>
            )}

            {/* ── Main Content ── */}
            <div className="flex-1 min-w-0 p-6 flex flex-col justify-center gap-1">
                <div className="flex items-center gap-3">
                    <span className="text-2xl font-black text-white font-heebo tracking-tighter uppercase">{dateStr}</span>
                    <span className="text-sm font-bold text-slate-500 tabular-nums">{timeStr}</span>
                    {isMultiTable && <span className="text-[9px] font-black text-white bg-indigo-500 px-2.5 py-1 rounded-lg border border-indigo-400 shadow-lg shadow-indigo-500/20 uppercase tracking-widest">{t('twoTables')}</span>}
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-400 truncate font-heebo mt-2 uppercase font-black tracking-widest">
                    <span className="text-indigo-400/90">{variantStr}</span>
                    <span className="text-slate-800">·</span>
                    <span className="text-emerald-400/90">{stakesStr}</span>
                </div>
            </div>

            {isRtl && (
                <div className="w-[4.5rem] flex-shrink-0 bg-emerald-500/10 border-l-[3px] border-l-emerald-500 flex flex-col items-center justify-center py-4 px-1 gap-1 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 via-transparent to-transparent" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_12px_rgba(52,211,153,0.8)]" />
                    <span className="text-[12px] tracking-tight font-black text-emerald-400 tabular-nums leading-none mt-1">{pad(h)}:{pad(m)}</span>
                    <span className="text-[8px] font-black text-emerald-300/80 tracking-[0.2em] uppercase mt-1">{t('live')}</span>
                </div>
            )}
            {!hasSelection && (
                <button onClick={e => { e.stopPropagation(); onDelete(session.id); }}
                    className={clsx("absolute top-4 p-2 text-slate-700 hover:text-rose-400 hover:bg-rose-400/10 rounded-xl transition-all z-10 opacity-0 group-hover:opacity-100", isRtl ? "left-4" : "right-4")}>
                    <Trash2 className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}

function UpcomingCard({ session, onNavigate, onStart, onDelete, isSelected, onSelect, hasSelection }) {
    const { t, language, isRtl } = useLanguage();
    const { d, h, m } = useCountdown(session.startTime);
    const isPastDue = new Date() >= new Date(session.startTime);
    const dateStr = new Date(session.startTime).toLocaleDateString(
        language === 'he' ? 'he-IL' : 'en-US',
        { weekday: 'short', day: 'numeric', month: 'short' }
    );
    const timeStr = new Date(session.startTime).toLocaleTimeString(
        language === 'he' ? 'he-IL' : 'en-US',
        { hour: '2-digit', minute: '2-digit' }
    );
    const isMultiTable = session.capacity === '2 Tables';
    const variantStr = isMultiTable
        ? `${session.table1Config?.variant?.includes('PLO') ? 'PLO' : 'NLH'} / ${session.table2Config?.variant?.includes('PLO') ? 'PLO' : 'NLH'}`
        : session.variant?.includes('PLO') ? 'PLO' : 'NLH';
    const stakesStr = isMultiTable
        ? `${session.table1Config?.stakes || '?'} & ${session.table2Config?.stakes || '?'}`
        : session.stakes;

    return (
        <div className={clsx(
            'w-full relative flex items-stretch rounded-3xl overflow-hidden transition-all cursor-pointer bg-slate-900 border active:scale-[0.99] group shadow-xl hover:border-amber-500/40',
            isSelected ? 'border-amber-500 ring-2 ring-amber-500/20' : 'border-white/5'
        )} onClick={() => { if (hasSelection) onSelect(session.id); else onNavigate(session.id); }}>

            {/* Selection checkbox */}
            <div className={clsx("absolute top-4 z-20", isRtl ? "right-4" : "left-4")}>
                <button onClick={e => { e.stopPropagation(); onSelect(session.id); }}
                    className={clsx('w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all',
                        (hasSelection || isSelected) ? 'opacity-100 scale-110' : 'opacity-20 hover:opacity-100',
                        isSelected ? 'bg-amber-500 border-amber-500' : 'bg-slate-800/80 border-slate-700'
                    )}>
                    {isSelected && <Check className="w-4 h-4 text-slate-950 font-black" />}
                </button>
            </div>

            {/* ── Strip (Right in RTL, Left in LTR) ── */}
            {!isRtl && (
                <div className="w-[4.5rem] flex-shrink-0 bg-amber-500/10 border-r-[3px] border-r-amber-500 flex flex-col items-center justify-center py-4 px-1 gap-1 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-amber-500/10 via-transparent to-transparent" />
                    {isPastDue ? (
                        <button onClick={e => { e.stopPropagation(); onStart(session.id); }} className="flex flex-col items-center gap-1 z-10">
                            <PlayCircle className="w-7 h-7 text-emerald-400 animate-pulse" />
                            <span className="text-[10px] font-black text-emerald-400 tracking-wider">{t('start')}</span>
                        </button>
                    ) : (
                        <>
                            <CalendarClock className="w-5 h-5 text-amber-500/60" />
                            <span className="text-[12px] tracking-tight font-black text-amber-400 tabular-nums leading-none mt-1">
                                {d > 0 ? `${d}d` : `${pad(h)}:${pad(m)}`}
                            </span>
                            <span className="text-[8px] font-black text-slate-500 tracking-[0.1em] mt-1 uppercase">{t('soon')}</span>
                        </>
                    )}
                </div>
            )}

            {/* ── Main Content ── */}
            <div className="flex-1 min-w-0 p-6 flex flex-col justify-center gap-1">
                <div className="flex items-center gap-3">
                    <span className="text-2xl font-black text-white font-heebo tracking-tighter uppercase">{dateStr}</span>
                    <span className="text-sm font-bold text-slate-500 tabular-nums">{timeStr}</span>
                    {isMultiTable && <span className="text-[9px] font-black text-white bg-amber-600 px-2.5 py-1 rounded-lg border border-amber-500 shadow-lg shadow-amber-500/20 uppercase tracking-widest">{t('twoTables')}</span>}
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-400 truncate font-heebo mt-2 uppercase font-black tracking-widest">
                    <span className="text-indigo-400/90">{variantStr}</span>
                    <span className="text-slate-800">·</span>
                    <span className="text-amber-400/90">{stakesStr}</span>
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20">
                        <Clock className="w-3 h-3 text-amber-500" />
                        <span className="text-[10px] font-black text-amber-500 uppercase tracking-wider">{t('soon')}</span>
                    </div>
                </div>
            </div>

            {isRtl && (
                <div className="w-[4.5rem] flex-shrink-0 bg-amber-500/10 border-l-[3px] border-l-amber-500 flex flex-col items-center justify-center py-4 px-1 gap-1 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-amber-500/10 via-transparent to-transparent" />
                    {isPastDue ? (
                        <button onClick={e => { e.stopPropagation(); onStart(session.id); }} className="flex flex-col items-center gap-1 z-10">
                            <PlayCircle className="w-7 h-7 text-emerald-400 animate-pulse" />
                            <span className="text-[10px] font-black text-emerald-400 tracking-wider">{t('start')}</span>
                        </button>
                    ) : (
                        <>
                            <CalendarClock className="w-5 h-5 text-amber-500/60" />
                            <span className="text-[12px] tracking-tight font-black text-amber-400 tabular-nums leading-none mt-1">
                                {d > 0 ? `${d}d` : `${pad(h)}:${pad(m)}`}
                            </span>
                            <span className="text-[8px] font-black text-slate-500 tracking-[0.1em] mt-1 uppercase">{t('soon')}</span>
                        </>
                    )}
                </div>
            )}

            {/* Selection checkbox */}
            <div className={clsx("absolute top-4 z-20", isRtl ? "right-4" : "left-4")}>
                <button onClick={e => { e.stopPropagation(); onSelect(session.id); }}
                    className={clsx('w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all',
                        (hasSelection || isSelected) ? 'opacity-100 scale-110' : 'opacity-20 hover:opacity-100',
                        isSelected ? 'bg-amber-500 border-amber-500' : 'bg-slate-800/80 border-slate-700'
                    )}>
                    {isSelected && <Check className="w-4 h-4 text-slate-950 font-black" />}
                </button>
            </div>
            {!hasSelection && (
                <button onClick={e => { e.stopPropagation(); e.preventDefault(); onDelete(session.id); }}
                    className={clsx("absolute top-4 p-2 text-slate-700 hover:text-rose-400 hover:bg-rose-400/10 rounded-xl transition-all z-10 opacity-0 group-hover:opacity-100", isRtl ? "left-4" : "right-4")}>
                    <Trash2 className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}

function CompletedCard({ session, onNavigate, onDelete, isSelected, onSelect, hasSelection }) {
    const { t, language } = useLanguage();
    const net = session.netBalance || 0;
    const dateStr = new Date(session.startTime || session.endedAt).toLocaleDateString(
        language === 'he' ? 'he-IL' : 'en-US',
        { weekday: 'short', day: 'numeric', month: 'short' }
    );
    const timeStr = new Date(session.startTime || session.endedAt).toLocaleTimeString(
        language === 'he' ? 'he-IL' : 'en-US',
        { hour: '2-digit', minute: '2-digit' }
    );
    const isMultiTable = session.capacity === '2 Tables';
    const variantStr = isMultiTable
        ? `${session.table1Config?.variant?.includes('PLO') ? 'PLO' : 'NLH'} / ${session.table2Config?.variant?.includes('PLO') ? 'PLO' : 'NLH'}`
        : session.variant?.includes('PLO') ? 'PLO' : 'NLH';
    const stakesStr = isMultiTable
        ? `${session.table1Config?.stakes || '?'} & ${session.table2Config?.stakes || '?'}`
        : session.stakes;

    return (
        <div className={clsx(
            'w-full relative flex items-stretch rounded-3xl overflow-hidden transition-all cursor-pointer bg-slate-900 border active:scale-[0.99] group shadow-lg hover:border-white/10 opacity-80 grayscale-[0.2]',
            isSelected ? 'border-slate-500 ring-2 ring-slate-500/20' : 'border-white/5'
        )} onClick={() => { if (hasSelection) onSelect(session.id); else onNavigate(session.id); }}>

            {/* Selection checkbox */}
            <div className={clsx("absolute top-4 z-20", isRtl ? "right-4" : "left-4")}>
                <button onClick={e => { e.stopPropagation(); onSelect(session.id); }}
                    className={clsx('w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all',
                        (hasSelection || isSelected) ? 'opacity-100 scale-110' : 'opacity-20 hover:opacity-100',
                        isSelected ? 'bg-white border-white' : 'bg-slate-800/80 border-slate-700'
                    )}>
                    {isSelected && <Check className="w-4 h-4 text-slate-950 font-black" />}
                </button>
            </div>

            {/* ── Strip (Right in RTL, Left in LTR) ── */}
            {!isRtl && (
                <div className="w-[4.5rem] flex-shrink-0 bg-slate-800/80 border-r-[3px] border-r-slate-600 flex flex-col items-center justify-center py-4 px-1 gap-1 relative overflow-hidden">
                    <div className="px-3 py-1 rounded-full bg-slate-800/50 border border-white/5">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider leading-none">{t('done')}</span>
                    </div>
                    <span className={clsx('text-[13px] font-black tabular-nums leading-none', net >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
                        {net >= 0 ? '+' : ''}₪{Math.abs(net).toLocaleString()}
                    </span>
                </div>
            )}

            {/* ── Main Content ── */}
            <div className="flex-1 min-w-0 p-6 flex flex-col justify-center gap-1">
                <div className="flex items-center gap-3">
                    <span className="text-2xl font-black text-slate-300 font-heebo tracking-tighter uppercase">{dateStr}</span>
                    <span className="text-sm font-bold text-slate-500 tabular-nums">{timeStr}</span>
                    {isMultiTable && <span className="text-[8px] font-black text-white/40 bg-white/5 px-2 py-0.5 rounded-full border border-white/10 uppercase tracking-widest">2T</span>}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 truncate font-heebo mt-1 uppercase font-black tracking-widest">
                    <span className="text-indigo-400/50">{variantStr}</span>
                    <span className="text-slate-800">·</span>
                    <span className="text-amber-400/50">{stakesStr}</span>
                </div>
            </div>

            {isRtl && (
                <div className="w-[4.5rem] flex-shrink-0 bg-slate-800/80 border-l-[3px] border-l-slate-600 flex flex-col items-center justify-center py-4 px-1 gap-1 relative overflow-hidden">
                    <div className="px-3 py-1 rounded-full bg-slate-800/50 border border-white/5">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider leading-none">{t('done')}</span>
                    </div>
                    <span className={clsx('text-[13px] font-black tabular-nums leading-none', net >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
                        {net >= 0 ? '+' : ''}₪{Math.abs(net).toLocaleString()}
                    </span>
                </div>
            )}

            {/* Selection checkbox */}
            <div className="absolute top-4 right-4 z-20">
                <button onClick={e => { e.stopPropagation(); onSelect(session.id); }}
                    className={clsx('w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all',
                        (hasSelection || isSelected) ? 'opacity-100 scale-110' : 'opacity-20 hover:opacity-100',
                        isSelected ? 'bg-white border-white' : 'bg-slate-800/80 border-slate-700'
                    )}>
                    {isSelected && <Check className="w-4 h-4 text-slate-950 font-black" />}
                </button>
            </div>
            {!hasSelection && (
                <button onClick={e => { e.stopPropagation(); onDelete(session.id); }}
                    className="absolute top-4 left-4 p-2 text-slate-700 hover:text-rose-400 hover:bg-rose-400/10 rounded-xl transition-all z-10 opacity-0 group-hover:opacity-100">
                    <Trash2 className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}

// ─── Sessions Page ────────────────────────────────────────────────
export const Dashboard = () => {
    const { t, isRtl } = useLanguage();
    const navigate = useNavigate();
    const user = auth.currentUser;
    const hostId = user?.uid;
    const isGuest = user?.email === 'guest@ledger.com';

    const [allSessions, setAllSessions] = useState([]);
    const [lastCompleted, setLastCompleted] = useState([]);
    const [showNewModal, setShowNewModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [startingId, setStartingId] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [sessionIdToDelete, setSessionIdToDelete] = useState(null);
    const [isBulkDelete, setIsBulkDelete] = useState(false);

    // ... (logic remains same)
    
    useEffect(() => {
        if (!hostId) return;
        const unsub = subscribeGames(hostId, (games) => {
            setAllSessions(games);
            setLoading(false);
        });
        return unsub;
    }, [hostId]);

    // ... (demo cleanup logic remains same)

    const activeSessions = allSessions.filter(g => g.status === 'ongoing');
    const upcomingSessions = allSessions.filter(g => g.status === 'upcoming').sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    const handleStart = async (id) => {
        if (!hostId || startingId) return;
        setStartingId(id);
        try { await startGame(hostId, id); }
        catch (err) { console.error(err); }
        finally { setStartingId(null); }
    };

    const goTo = (id) => navigate(`/session/${id}`);

    const handleDeleteClick = (id) => {
        if (!hostId) return;
        setSessionIdToDelete(id);
        setIsBulkDelete(false);
        setIsDeleteModalOpen(true);
    };

    const handleBulkDelete = () => {
        if (selectedIds.size === 0) return;
        setIsBulkDelete(true);
        setSessionIdToDelete(null);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        setIsDeleteModalOpen(false);
        try {
            if (isBulkDelete) {
                const idsToDelete = Array.from(selectedIds);
                setAllSessions(prev => prev.filter(s => !selectedIds.has(s.id)));
                await deleteMultipleSessions(hostId, idsToDelete);
                setSelectedIds(new Set());
            } else if (sessionIdToDelete) {
                setAllSessions(prev => prev.filter(s => s.id !== sessionIdToDelete));
                await deleteGame(hostId, sessionIdToDelete);
                setSessionIdToDelete(null);
            }
        } catch (err) {
            console.error('Delete failed:', err);
        }
    };

    const handleToggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleBulkDuplicate = async () => {
        if (!hostId || selectedIds.size === 0) return;
        setLoading(true);
        try {
            await duplicateMultipleSessions(hostId, Array.from(selectedIds));
            setSelectedIds(new Set());
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const sortedSessions = [
        ...activeSessions,
        ...upcomingSessions,
        ...lastCompleted,
    ];

    return (
        <div className="flex flex-col min-h-[100dvh] bg-slate-950 text-white font-outfit" dir={isRtl ? 'rtl' : 'ltr'}>
            <TopNav />

            {/* Guest Welcome Toast */}
            {isGuest && (
                <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="bg-gradient-to-r from-amber-500/90 to-amber-600/90 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/20 shadow-2xl flex items-center gap-3">
                        <Zap className="w-5 h-5 text-white animate-pulse" />
                        <span className="text-white font-black text-sm whitespace-nowrap tracking-tight">
                            {t('guestWelcome')}
                        </span>
                    </div>
                </div>
            )}

            <div className="flex flex-col flex-1 min-h-0 w-full max-w-xl mx-auto px-4 py-6">

                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <CalendarClock className="w-5 h-5 text-amber-500" />
                        <div>
                            <h1 className="text-[clamp(1.4rem,4vw,1.8rem)] font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600 leading-none font-heebo uppercase">
                                {t('sessionManager')}
                            </h1>
                            <p className="text-xs text-slate-500 mt-0.5 font-heebo">{t('sessionManagerSubtitle')}</p>
                        </div>
                    </div>
                    {!isGuest && (
                        <button
                            onClick={() => setShowNewModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-black text-xs rounded-xl transition-colors shadow-lg shadow-purple-500/20 font-heebo"
                        >
                            <Plus className="w-4 h-4" />
                            {t('newSession')}
                        </button>
                    )}
                </div>

                {/* ── Session List ── */}
                <div className="flex-1 overflow-y-auto min-h-0 space-y-2 pb-4">
                    {loading ? (
                        [1, 2, 3].map(i => <div key={i} className="h-[4.5rem] bg-slate-900/50 rounded-3xl animate-pulse" />)
                    ) : sortedSessions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-600">
                            <CalendarClock className="w-12 h-12 mb-4 opacity-20" />
                            <p className="text-sm font-heebo mb-1">{t('noSessionsYet')}</p>
                            <p className="text-[11px] text-slate-700 font-heebo">{t('clickNewSession')}</p>
                        </div>
                    ) : (
                        <>
                            {activeSessions.map(s => (
                                <ActiveCard
                                    key={s.id}
                                    session={s}
                                    onNavigate={goTo}
                                    onDelete={handleDeleteClick}
                                    isSelected={selectedIds.has(s.id)}
                                    onSelect={handleToggleSelect}
                                    hasSelection={selectedIds.size > 0}
                                />
                            ))}

                            {upcomingSessions.length > 0 && (
                                <>
                                    {activeSessions.length > 0 && (
                                        <div className="flex items-center gap-3 py-1">
                                            <div className="flex-1 h-px bg-white/5" />
                                            <span className="text-[9px] font-black text-slate-700 uppercase tracking-[0.3em]">{t('upcoming')}</span>
                                            <div className="flex-1 h-px bg-white/5" />
                                        </div>
                                    )}
                                    {upcomingSessions.map(s => (
                                        <UpcomingCard
                                            key={s.id}
                                            session={s}
                                            onNavigate={goTo}
                                            onStart={handleStart}
                                            onDelete={handleDeleteClick}
                                            isSelected={selectedIds.has(s.id)}
                                            onSelect={handleToggleSelect}
                                            hasSelection={selectedIds.size > 0}
                                        />
                                    ))}
                                </>
                            )}

                            {lastCompleted.length > 0 && (
                                <>
                                    <div className="flex items-center gap-3 py-1">
                                        <div className="flex-1 h-px bg-white/5" />
                                        <span className="text-[9px] font-black text-slate-700 uppercase tracking-[0.3em]">{t('recent')}</span>
                                        <div className="flex-1 h-px bg-white/5" />
                                    </div>
                                    {lastCompleted.map(s => (
                                        <CompletedCard
                                            key={s.id}
                                            session={s}
                                            onNavigate={goTo}
                                            onDelete={handleDeleteClick}
                                            isSelected={selectedIds.has(s.id)}
                                            onSelect={handleToggleSelect}
                                            hasSelection={selectedIds.size > 0}
                                        />
                                    ))}
                                    <button
                                        onClick={() => navigate('/history')}
                                        className="w-full py-3 text-[10px] font-black text-slate-600 hover:text-[#7C3AED] uppercase tracking-[0.3em] transition-colors font-heebo"
                                    >
                                        {t('viewFullHistory')}
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Bulk Action Bar - Sticky/Floating centered glassmorphic */}
            {selectedIds.size > 0 && (
                <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] w-full max-w-lg px-4 animate-in slide-in-from-bottom-12 duration-500">
                    <div className="bg-slate-900/60 backdrop-blur-3xl border border-white/10 p-4 rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-[#7C3AED] flex items-center justify-center border border-white/20 shadow-lg shadow-purple-500/20">
                                <span className="text-xl font-black text-white tabular-nums">{selectedIds.size}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-black text-white uppercase tracking-tight leading-none">
                                    {t('itemsSelected', { count: selectedIds.size })}
                                </span>
                                <button 
                                    onClick={() => setSelectedIds(new Set())}
                                    className="text-[10px] font-bold text-slate-500 hover:text-white uppercase tracking-widest text-right transition-colors mt-1"
                                >
                                    {t('clearSelection') || 'Clear'}
                                </button>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleBulkDuplicate}
                                className="w-12 h-12 flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-[#7C3AED] transition-all active:scale-95 shadow-lg group relative"
                                title={t('duplicateItems')}
                            >
                                <RefreshCw className={clsx("w-5 h-5", loading && "animate-spin")} />
                                <div className="absolute bottom-full mb-3 hidden group-hover:block bg-slate-800 text-[10px] text-white px-2 py-1 rounded whitespace-nowrap">{t('duplicateItems')}</div>
                            </button>
                            <button
                                onClick={handleBulkDelete}
                                className="px-6 py-3.5 bg-rose-500 hover:bg-rose-600 rounded-2xl text-xs font-black text-white transition-all active:scale-95 shadow-xl shadow-rose-500/20 uppercase tracking-widest flex items-center gap-2"
                            >
                                <Trash2 className="w-5 h-5" />
                                <span>{t('deleteItems')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isDeleteModalOpen && (
                <ConfirmModal 
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={confirmDelete}
                    title={isBulkDelete ? t('confirmBulkDelete') : t('confirmDelete')}
                    message={isBulkDelete 
                        ? t('confirmBulkDeleteMsg', { count: selectedIds.size })
                        : t('confirmDeleteMsg')
                    }
                    confirmText={t('delete')}
                    type="danger"
                />
            )}

            {showNewModal && (
                <NewSessionModal onClose={() => setShowNewModal(false)} onCreated={() => setShowNewModal(false)} />
            )}
        </div>
    );
};
